import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';

export enum CredentialsStatus {
  CREATED = 'CREATED',
  AUTHENTICATING = 'AUTHENTICATING',
  AWAITING_ACCOUNTS_SELECTION = 'AWAITING_ACCOUNTS_SELECTION',
  AWAITING_MOBILE_BANKID_AUTHENTICATION = 'AWAITING_MOBILE_BANKID_AUTHENTICATION',
  AWAITING_SUPPLEMENTAL_INFORMATION = 'AWAITING_SUPPLEMENTAL_INFORMATION',
  AWAITING_THIRD_PARTY_APP_AUTHENTICATION = 'AWAITING_THIRD_PARTY_APP_AUTHENTICATION',
  UPDATING = 'UPDATING',
  UPDATED = 'UPDATED',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  TEMPORARY_ERROR = 'TEMPORARY_ERROR',
  NOT_IMPLEMENTED_ERROR = 'NOT_IMPLEMENTED_ERROR',
}

export enum CredentialsType {
  PASSWORD = 'PASSWORD',
  MOBILE_BANKID = 'MOBILE_BANKID',
  KEYFOB = 'KEYFOB',
  FRAUD = 'FRAUD',
  THIRD_PARTY_APP = 'THIRD_PARTY_APP',
}

export enum AwaitingScenario {
  AUTHORIZE,
  ACCOUNTS,
  PAYMENT,
}

export enum AuthenticationPromptType {
  NONE,
  LOCAL,
  REMOTE,
}

export type ThirdpartyAuthenticationPayload = {
  desktop?: {
    url: string;
  };
  ios?: {
    deepLinkUrl: string;
  };
  android?: {
    intent: string;
  };
};

type CredentialsFields = { [key: string]: string };

// This enum is coming from:
// https://github.com/tink-ab/tink-backend/blob/master/src/libraries/connectivity_errors/proto/errors.proto
export enum DetailedErrorReason {
  // TinkSideErrors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  TINK_INTERNAL_SERVER_ERROR = 'TINK_INTERNAL_SERVER_ERROR',
  OPERATION_NOT_SUPPORTED = 'OPERATION_NOT_SUPPORTED',
  AUTHENTICATION_METHOD_NOT_SUPPORTED = 'AUTHENTICATION_METHOD_NOT_SUPPORTED',

  // enum ProviderErrors
  PROVIDER_UNAVAILABLE = 'PROVIDER_UNAVAILABLE',
  LICENSED_PARTY_REJECTED = 'LICENSED_PARTY_REJECTED',

  // UserLoginErrors
  THIRD_PARTY_AUTHENTICATION_UNAVAILABLE = 'THIRD_PARTY_AUTHENTICATION_UNAVAILABLE',
  STATIC_CREDENTIALS_INCORRECT = 'STATIC_CREDENTIALS_INCORRECT',
  DYNAMIC_CREDENTIALS_INCORRECT = 'DYNAMIC_CREDENTIALS_INCORRECT',
  DYNAMIC_CREDENTIALS_FLOW_CANCELLED = 'DYNAMIC_CREDENTIALS_FLOW_CANCELLED',
  DYNAMIC_CREDENTIALS_FLOW_TIMEOUT = 'DYNAMIC_CREDENTIALS_FLOW_TIMEOUT',
  USER_NOT_A_CUSTOMER = 'USER_NOT_A_CUSTOMER',
  USER_CONCURRENT_LOGINS = 'USER_CONCURRENT_LOGINS',
  USER_BLOCKED = 'USER_BLOCKED',

  // AuthorizationErrors
  ACTION_NOT_PERMITTED = 'ACTION_NOT_PERMITTED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  USER_ACTION_REQUIRED_UNSIGNED_AGREEMENT = 'USER_ACTION_REQUIRED_UNSIGNED_AGREEMENT',
  USER_ACTION_REQUIRED = 'USER_ACTION_REQUIRED',

  // AccountInformationErrors
  NO_ACCOUNTS = 'NO_ACCOUNTS',
  NOT_ENOUGH_DATA_TO_PROVIDE_PRODUCT = 'NOT_ENOUGH_DATA_TO_PROVIDE_PRODUCT',
}

export enum DetailedErrorType {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  TINK_SIDE_ERROR = 'TINK_SIDE_ERROR',
  PROVIDER_ERROR = 'PROVIDER_ERROR',
  USER_LOGIN_ERROR = 'USER_LOGIN_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  ACCOUNT_INFORMATION_ERROR = 'ACCOUNT_INFORMATION_ERROR',
}

export type DetailedError = {
  type: DetailedErrorType;
  displayMessage: string;
  details: {
    reason: DetailedErrorReason;
    retryable: boolean;
  };
};

export type CredentialsEntity<T extends CredentialsStatus = CredentialsStatus> = {
  id: string;
  providerName: string;
  status: T;
  statusPayload?: string;
  statusUpdated?: number;
  type: CredentialsType;
  userId: string;
  fields: CredentialsFields;
  supplementalInformation?: string;
  appUri?: string;
  detailedError?: DetailedError;
  updated?: number;
};

export type AccountSelectionIdentifiers = {
  type: AccountSelectionIdentifierTypes;
  value: string;
};

export enum AccountSelectionIdentifierTypes {
  ACCOUNT_NUMBER = 'ACCOUNT_NUMBER',
  ACCOUNT_NAME = 'ACCOUNT_NAME',
}

export type AccountSelectionResponse = {
  id: string;
  accountBalance: {
    currency: string;
    value: number;
  };
  identifiers: AccountSelectionIdentifiers[];
};

export enum ResetCredentialsStatus {
  TRYING_TO_ABORT = 'TRYING_TO_ABORT',
  ABORTING_OPERATION_FAILED = 'ABORTING_OPERATION_FAILED',
  ABORTING_OPERATION_SUCCEEDED = 'ABORTING_OPERATION_SUCCEEDED',
}

export type ResetCredentialsResponse = {
  requestStatus: ResetCredentialsStatus;
};

export const resetCredentialsStatusSucceeded = (response: ResetCredentialsResponse) => {
  return response.requestStatus === ResetCredentialsStatus.ABORTING_OPERATION_SUCCEEDED;
};

export const resetCredentialsStatusFailed = (response: ResetCredentialsResponse) => {
  return response.requestStatus === ResetCredentialsStatus.ABORTING_OPERATION_FAILED;
};

export const Credentials = {
  getUsername: (credentials: CredentialsEntity) => {
    if (!credentials.fields) {
      return null;
    }

    if (!credentials.fields.username) {
      return null;
    }

    return credentials.fields.username;
  },

  hasError: (credentials?: CredentialsEntity) => {
    switch (credentials?.status) {
      case CredentialsStatus.AUTHENTICATION_ERROR:
      case CredentialsStatus.NOT_IMPLEMENTED_ERROR:
      case CredentialsStatus.TEMPORARY_ERROR:
        return true;
      default:
        return false;
    }
  },

  isSwedbankSavingsbankError: (credentials: CredentialsEntity) => {
    return (
      (credentials.statusPayload?.includes('You do not have any accounts at Sparbankerna') ||
        credentials.statusPayload?.includes('You do not have any accounts at Swedbank') ||
        credentials.statusPayload?.includes('Du har inga konton hos Sparbankerna') ||
        credentials.statusPayload?.includes('Du har inga konton hos Swedbank')) ??
      false
    );
  },

  isMobileBankId: (credentials: CredentialsEntity) => {
    return credentials.type === CredentialsType.MOBILE_BANKID;
  },

  isThirdPartyApp: (credentials: CredentialsEntity) => {
    return credentials.type === CredentialsType.THIRD_PARTY_APP;
  },

  isUpdating: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.UPDATING> => {
    return credentials?.status === CredentialsStatus.UPDATING;
  },

  isUpdated: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.UPDATED> => {
    return credentials?.status === CredentialsStatus.UPDATED;
  },

  isAuthenticating: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AUTHENTICATING> => {
    return credentials?.status === CredentialsStatus.AUTHENTICATING;
  },

  isAuthenticationError: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AUTHENTICATION_ERROR> => {
    return credentials?.status === CredentialsStatus.AUTHENTICATION_ERROR;
  },

  isCreated: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.CREATED> => {
    return credentials?.status === CredentialsStatus.CREATED;
  },

  isAwaitingMobileBankId: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AWAITING_MOBILE_BANKID_AUTHENTICATION> => {
    return credentials?.status === CredentialsStatus.AWAITING_MOBILE_BANKID_AUTHENTICATION;
  },

  isAwaitingAccountsSelection: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AWAITING_ACCOUNTS_SELECTION> => {
    return credentials?.status === CredentialsStatus.AWAITING_ACCOUNTS_SELECTION;
  },

  isAwaitingSupplementalInformation: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AWAITING_SUPPLEMENTAL_INFORMATION> => {
    return credentials?.status === CredentialsStatus.AWAITING_SUPPLEMENTAL_INFORMATION;
  },

  isAwaitingThirdPartyAppAuthentication: (
    credentials?: CredentialsEntity
  ): credentials is CredentialsEntity<CredentialsStatus.AWAITING_THIRD_PARTY_APP_AUTHENTICATION> => {
    return credentials?.status === CredentialsStatus.AWAITING_THIRD_PARTY_APP_AUTHENTICATION;
  },

  toJSON: (credentials: CredentialsEntity) => {
    return JSON.stringify(credentials);
  },

  isFinished: (
    credentials: CredentialsEntity | undefined,
    configuration: ConfigurationModel
  ): credentials is CredentialsEntity => {
    const hasFlag = configuration.featureFlags.includes(
      ConfigurationFeatureFlag.SKIP_UPDATING_CREDENTIALS
    );

    if (hasFlag) {
      return Credentials.isUpdating(credentials);
    }

    return Credentials.isUpdated(credentials);
  },
};
